import {
  AHB_MESSAGE_TYPE,
  AHB_NAMESPACES_BY_VERSION,
  AHB_PROCEDURE_ID,
  AHB_SUBPROCEDURE,
  AHB_WITHDRAW_REASON,
} from "core/consts";
import type { SchemaVersion } from "core/types";
import { format, utcToZonedTime } from "date-fns-tz";
import { AHBWithdrawPartialPayload } from "onprem/hooks";
import { v4 as uuidv4 } from "uuid";
import { nullsToUndefined } from "./shared";

const timeZone = "Europe/Berlin";

function formatDateYYYYMMDD(
  date: Date | number | null | undefined,
): string | undefined {
  if (!date) {
    return undefined;
  }

  let dateObject: Date;

  if (typeof date === "number") {
    dateObject = new Date(date * 1000);
  } else {
    dateObject = date;
  }

  const zonedDate = utcToZonedTime(dateObject, timeZone);

  return format(zonedDate, "yyyyMMdd");
}

export function mapToRehabWithdrawal({
  isProd,
  unsanitizedForm,
  schemaVersion,
}: {
  isProd: boolean;
  unsanitizedForm: AHBWithdrawPartialPayload;
  schemaVersion: SchemaVersion;
}) {
  const form = nullsToUndefined(unsanitizedForm);
  const now = new Date();
  const zonedDate = utcToZonedTime(now, timeZone);
  const erstellungsdatum = formatDateYYYYMMDD(now);
  const erstellungszeit = format(zonedDate, "HHmmss");
  const dateiID = uuidv4();

  if (!erstellungsdatum) {
    throw new Error("Missing gdb:Erstellungsdatum");
  }

  const ns = AHB_NAMESPACES_BY_VERSION[schemaVersion];

  return {
    Nutzdaten: {
      "@xmlns": ns.xmlns,
      "@xmlns:gdb": ns.xmlnsGdb,
      "@xmlns:aar": ns.xmlnsAar,
      "@xmlns:arh": ns.xmlnsArh,
      "@xmlns:feh": ns.xmlnsFeh,
      "@xmlns:sto": ns.xmlnsSto,
      Header: {
        "gdb:Absender": {
          "gdb:Krankenhaus": {
            "gdb:Institutionskennzeichen": form.hospital_ik_number
              ? String(form.hospital_ik_number)
              : "",
          },
        },
        "gdb:Empfaenger": {
          "gdb:Krankenkasse": {
            "gdb:Institutionskennzeichen": form.insurance_ik_number
              ? String(form.insurance_ik_number)
              : "",
          },
        },
        "gdb:Erstellungsdatum": erstellungsdatum,
        "gdb:Erstellungszeit": erstellungszeit,
        "gdb:Datei_ID": dateiID,
        "gdb:Verfahrenskennung": isProd
          ? AHB_PROCEDURE_ID.PRODUCTION
          : AHB_PROCEDURE_ID.TEST,
        "gdb:Unterverfahren": AHB_SUBPROCEDURE,
        "gdb:Nachrichtentyp": AHB_MESSAGE_TYPE.WITHDRAWAL,
        "gdb:Logische_Version": schemaVersion,
        "gdb:Dateinummer": form.documentNumber,
      },
      Body: {
        Stornonachricht: {
          "sto:Datei_ID": form.request_message_id,
          "sto:Begruendung": {
            "sto:Grund": form.withdraw_reason,
            ...(form.withdraw_reason === AHB_WITHDRAW_REASON.OTHER &&
            form.withdraw_reason_other
              ? { "sto:Erlaeuterung": form.withdraw_reason_other }
              : {}),
          },
        },
      },
    },
  };
}
